import React, { useContext, useEffect, useState } from 'react';
import { Card, Skeleton, Table, Tag, message, theme } from 'antd';
import type { TableColumnsType } from 'antd';
import type { Dayjs } from 'dayjs';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import getOverviewCalls from '../../../../domain/use_cases/leedflow/overview/getOverviewCalls';
import { formatCallNumber, renderConfidential, renderDate, renderDevice, renderDirection, renderPlatform, renderLine } from './callCells';
import CallDetailsModal from './CallDetailsModal';
import type { LastCallEntry } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface LastCallsTableProps {
  /**
   * Les appels les plus récents de la période, tels que les porte la charge
   * utile de l'aperçu (`LAST_CALLS_LIMIT` lignes). Ils servent les premières
   * pages sans aller-retour supplémentaire ; au-delà, la table va les chercher
   * page par page sur `/leedflow/overview-calls`.
   */
  rows: LastCallEntry[];
  /** Nombre d'appels de la période, tous confondus — borne la pagination. */
  total?: number;
  status: ContextStatus;
  /** Même période et même filtre que l'aperçu, pour les pages suivantes. */
  period?: [Dayjs | null, Dayjs | null];
  subscriptionIds?: string[];
  exclude?: string[];
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}min ${s}s` : `${s}s`;
};

const LastCallsTable: React.FC<LastCallsTableProps> = ({ rows, total, status, period, subscriptionIds, exclude }) => {
  const { token } = useToken();
  const authContext = useContext(AuthenticationContext);
  const [selected, setSelected] = useState<LastCallEntry | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fetched, setFetched] = useState<LastCallEntry[]>([]);
  const [fetching, setFetching] = useState(false);
  const [filters, setFilters] = useState({ period, subscriptionIds, exclude });

  // Une page 8 n'a aucun sens sur une autre période : on revient en tête. En
  // rendu et non dans un effet — au montage la page vaut déjà 1, donc rien à
  // rattraper, et un effet relancerait un rendu de plus à chaque filtre.
  if (filters.period !== period || filters.subscriptionIds !== subscriptionIds || filters.exclude !== exclude) {
    setFilters({ period, subscriptionIds, exclude });
    setPage(1);
  }

  // La charge utile de l'aperçu couvre la page demandée tant qu'on reste dans
  // ses `rows` — ou qu'elle porte déjà toute la période. Le serveur n'est
  // sollicité que pour ce qu'elle ne contient pas : les premières pages
  // restent instantanées et gardent le cache de 5 min de `overview-stats`.
  const payloadCoversPage =
    (total !== undefined && rows.length >= total) || page * pageSize <= rows.length;

  useEffect(() => {
    if (payloadCoversPage) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const res = await getOverviewCalls(authContext.authentication, page, pageSize, period, subscriptionIds, exclude);
        if (!cancelled) setFetched(res);
      } catch (e) {
        if (!cancelled) {
          setFetched([]);
          message.error(e instanceof Error ? e.message : "Impossible de charger cette page d'appels.");
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authContext.authentication, payloadCoversPage, page, pageSize, period, subscriptionIds, exclude]);

  const pageRows = payloadCoversPage ? rows.slice((page - 1) * pageSize, page * pageSize) : fetched;

  // Le tag se porte sur le numéro du contact, pas sur la ligne Leedflow :
  // c'est le correspondant qui est confidentiel, l'abonnement ne l'est pas.
  const renderCustomer = (record: LastCallEntry): React.ReactNode => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {formatCallNumber(record.customer)}
      {record.confidential ? renderConfidential() : null}
    </span>
  );

  const columns: TableColumnsType<LastCallEntry> = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      render: (created: number) => renderDate(created),
    },
    {
      title: 'Type',
      dataIndex: 'direction',
      key: 'direction',
      render: (direction: string | null) => renderDirection(direction),
    },
    {
      title: 'État',
      dataIndex: 'missed',
      key: 'missed',
      // Aligné sur `isAnsweredCall` (backend/src/helpers/leedflow/overview-model.ts) :
      // `missed === false` ne suffit pas à dire « Décroché », il faut aussi
      // `duration > 0`. Sinon un appel `missed: false` + `duration: 0` (ni
      // compté dans answeredCalls ni dans missedCalls côté agrégat) s'affichait
      // « Décroché » — deux réponses différentes à la même question.
      render: (missed: boolean | null, record: LastCallEntry) => {
        if (missed === null) return <Tag>État inconnu</Tag>;
        if (missed) return <Tag color="error">Manqué</Tag>;
        return (record.duration ?? 0) > 0
          ? <Tag color="success">Décroché</Tag>
          : <Tag>Sans réponse</Tag>;
      },
    },
    // Le contact porte son numéro, l'abonnement son titulaire. Qui appelle qui
    // dépend du sens : sur un sortant, c'est l'abonnement l'appelant.
    {
      title: 'Appelant',
      key: 'caller',
      render: (_: unknown, record: LastCallEntry) =>
        record.direction === 'outbound' ? renderLine(record) : renderCustomer(record),
    },
    {
      title: 'Appelé',
      key: 'callee',
      render: (_: unknown, record: LastCallEntry) =>
        record.direction === 'outbound' ? renderCustomer(record) : renderLine(record),
    },
    {
      title: 'Plateforme',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string | null) => renderPlatform(platform),
    },
    {
      title: 'Appareil',
      dataIndex: 'device',
      key: 'device',
      render: (device: string | null) => renderDevice(device),
    },
    {
      title: 'Durée',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number | null) => formatDuration(duration ?? 0),
    },
  ];

  return (
    <Card title="Appels" style={{ marginTop: token.marginLG }}>
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table
          rowKey="id"
          dataSource={pageRows}
          columns={columns}
          loading={fetching}
          // Pagination pilotée par le serveur : `dataSource` ne porte que la
          // page courante, `total` dit combien d'appels la période compte.
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (count) => `${count} appels sur la période`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          size="small"
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => setSelected(record),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      <CallDetailsModal call={selected} onClose={() => setSelected(null)} />
    </Card>
  );
};

export default LastCallsTable;
